import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { TEMPLATES, TemplateSVG } from '../data/templates';
import Header from './Header';
import './TemplatePage.css';

function Toast({ anchorRef, visible }) {
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (visible && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({
        top:  rect.top + window.scrollY,
        left: rect.left + rect.width / 2,
      });
    }
  }, [visible, anchorRef]);

  if (!visible || !pos) return null;

  return ReactDOM.createPortal(
    <div style={{
      position: 'absolute',
      top: pos.top,
      left: pos.left,
      transform: 'translate(-50%, calc(-100% - 8px))',
      width: 'max-content',
      background: '#1a1a1a',
      color: 'white',
      fontSize: 12,
      fontWeight: 600,
      fontFamily: 'var(--font-body)',
      padding: '8px 14px',
      borderRadius: 8,
      zIndex: 9999,
      boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
      pointerEvents: 'none',
    }}>
      You're already using this template
      <div style={{
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid #1a1a1a',
      }}/>
    </div>,
    document.body
  );
}

export default function TemplatePage({ onSelectTemplate, onBack, onHome, compareMode = false, fullAppMode = false, user, disabledTemplateId, lastSaved, isSaving, saveFailed }) {
  const [toastId, setToastId] = useState(null);
  const activeRef = useRef(null);

  const handleCardClick = (t, el) => {
    if (t.id === disabledTemplateId) {
      activeRef.current = el;
      setToastId(t.id);
      setTimeout(() => setToastId(null), 2000);
      return;
    }
    onSelectTemplate(t);
  };

  const title    = compareMode ? 'Compare with a template' : 'Choose a resume template';
  const subtitle = compareMode
    ? 'Select a template to preview side by side with your current one.'
    : fullAppMode
      ? 'Pick a resume style first. You\'ll choose a matching cover letter style next.'
      : 'Pick a style to get started. You can customize everything in the editor.';

  return (
    <div className="template-page">
      <Header onHome={onHome ?? onBack} user={user} lastSaved={lastSaved} isSaving={isSaving} saveFailed={saveFailed} />

      <main className="template-page-main">
        <button className="template-page-back" onClick={onBack}>← Back</button>

        <div className="template-page-hero">
          {(compareMode || fullAppMode) && (
            <div className="template-compare-banner">
              {compareMode ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <rect x="2" y="3" width="9" height="18" rx="1"/><rect x="13" y="3" width="9" height="18" rx="1"/>
                  </svg>
                  Compare mode
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <rect x="2" y="7" width="20" height="14" rx="2"/>
                    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                  </svg>
                  Resume + Cover Letter — Step 1 of 2
                </>
              )}
            </div>
          )}
          <h1 className="template-page-title">{title}</h1>
          <p className="template-page-subtitle">{subtitle}</p>
        </div>

        <Toast anchorRef={activeRef} visible={!!toastId} />

        <div className="template-page-grid">
          {TEMPLATES.map(t => {
            const isCurrent = t.id === disabledTemplateId;
            return (
              <button
                key={t.id}
                className={`template-page-card ${isCurrent ? 'template-page-card-current' : ''}`}
                onClick={(e) => handleCardClick(t, e.currentTarget)}
              >
                <div className="template-preview-clip">
                  <TemplateSVG template={t} />
                </div>
                <div className="template-page-info">
                  <span className="template-page-name">{t.name}</span>
                  <span className="template-page-desc">{t.description}</span>
                </div>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
